// Pure session display and grouping for the booking flows that list or show
// sessions (reschedule, bundle dates, cancel). A port of what v1 gets from
// @obby/lib's Session__Display* helpers and SessionDateList, on Intl rather
// than moment, and in 24-hour time (this repo's UI convention).

import { safeTimezone, viewerTimezone } from "../../timezones";
import { SCHOOLS_HIDING_TUTORS } from "../../constants";
import { ordinal } from "../../dates";

/** The session fields these helpers read. */
export type SessionLike = {
  _id: string;
  startDateTime: string;
  duration?: number;
  isOnline?: boolean;
  availability?: number;
  locationId?: string;
  tutor?: string;
};

export type LocationLike = { _id: string; name?: string; area?: string };
export type TutorLike = {
  _id: string;
  fullName?: string;
  profile?: { imageUrl?: string; links?: { bio?: string } };
};

/** Where a session's times are shown: an online one in the viewer's zone
 * (they join from wherever they are), an in-person one in the school's. */
export function sessionTimezone(session: { isOnline?: boolean }, timezone: string, viewer = viewerTimezone()): string {
  return session.isOnline ? viewer : safeTimezone(timezone);
}

function parts(iso: string, timezone: string, options: Intl.DateTimeFormatOptions) {
  const formatted = new Intl.DateTimeFormat("en-GB", { timeZone: safeTimezone(timezone), ...options }).formatToParts(new Date(iso));
  const out: Record<string, string> = {};
  for (const part of formatted) out[part.type] = part.value;
  return out;
}

export { ordinal };

/** "Mon 5th March" (v1's "ddd Do MMMM"). */
export function formatShortDate(iso: string, timezone: string): string {
  const p = parts(iso, timezone, { weekday: "short", day: "numeric", month: "long" });
  return `${p.weekday} ${ordinal(Number(p.day))} ${p.month}`;
}

/** "Monday, 5th March" (v1's "dddd, Do MMMM"). */
export function formatLongDate(iso: string, timezone: string): string {
  const p = parts(iso, timezone, { weekday: "long", day: "numeric", month: "long" });
  return `${p.weekday}, ${ordinal(Number(p.day))} ${p.month}`;
}

/** "05/03/2026" (v1's "DD/MM/YYYY"). */
export function formatNumericDate(iso: string, timezone: string): string {
  const p = parts(iso, timezone, { day: "2-digit", month: "2-digit", year: "numeric" });
  return `${p.day}/${p.month}/${p.year}`;
}

/** "14:30", 24-hour. */
export function formatClock(iso: string, timezone: string): string {
  const p = parts(iso, timezone, { hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
  return `${p.hour}:${p.minute}`;
}

/** "14:30 - 16:00". */
export function formatTimeRange(iso: string, durationMinutes: number | undefined, timezone: string): string {
  const start = formatClock(iso, timezone);
  if (!durationMinutes) return start;
  const end = new Date(new Date(iso).getTime() + durationMinutes * 60_000).toISOString();
  return `${start} - ${formatClock(end, timezone)}`;
}

/** The zone's short name at that instant ("BST", "GMT+2"). */
export function formatOffsetName(iso: string, timezone: string): string {
  return parts(iso, timezone, { timeZoneName: "short" }).timeZoneName ?? "";
}

/** "Mon 5th March, 14:30 - 16:00", with the zone's name added when an
 * in-person session's zone isn't the viewer's (v1's
 * Session__DisplayDateTimeOffset). */
export function formatSessionDateTime(session: SessionLike, timezone: string, viewer = viewerTimezone()): string {
  const zone = sessionTimezone(session, timezone, viewer);
  let text = `${formatShortDate(session.startDateTime, zone)}, ${formatTimeRange(session.startDateTime, session.duration, zone)}`;
  if (!session.isOnline && zone !== viewer) text += ` (${formatOffsetName(session.startDateTime, zone)})`;
  return text;
}

/** "Sold out" / "1 spot left" / "4 spots left". */
export function describeAvailability(availability: number | undefined): string {
  const n = Number(availability ?? 0);
  if (n <= 0) return "Sold out";
  return `${n} ${n === 1 ? "spot" : "spots"} left`;
}

/** "Online", else the location's name or area. */
export function sessionLocationLabel(session: SessionLike, locations: LocationLike[] | undefined): string | undefined {
  if (session.isOnline) return "Online";
  const location = (locations ?? []).find((l) => l._id === session.locationId);
  return location?.name || location?.area || undefined;
}


/** One row of a session list, ready to show. */
export type SessionRow = {
  id: string;
  startDateTime: string;
  date: string;
  time: string;
  offset?: string;
  availability: string;
  soldOut: boolean;
  location?: string;
  tutorName?: string;
  tutorImageUrl?: string;
  tutorUrl?: string;
};

export function toSessionRow(
  session: SessionLike,
  args: { timezone: string; locations?: LocationLike[]; tutors?: TutorLike[]; schoolId?: string; viewer?: string },
): SessionRow {
  const viewer = args.viewer ?? viewerTimezone();
  const zone = sessionTimezone(session, args.timezone, viewer);
  const tutor = args.schoolId && SCHOOLS_HIDING_TUTORS.includes(args.schoolId)
    ? undefined
    : (args.tutors ?? []).find((t) => t._id === session.tutor);
  return {
    id: session._id,
    startDateTime: session.startDateTime,
    date: formatShortDate(session.startDateTime, zone),
    time: formatTimeRange(session.startDateTime, session.duration, zone),
    offset: !session.isOnline && zone !== viewer ? formatOffsetName(session.startDateTime, zone) : undefined,
    availability: describeAvailability(session.availability),
    soldOut: Number(session.availability ?? 0) <= 0,
    location: sessionLocationLabel(session, args.locations),
    tutorName: tutor?.fullName || undefined,
    tutorImageUrl: tutor?.profile?.imageUrl || undefined,
    tutorUrl: tutor?.profile?.links?.bio || undefined,
  };
}

export type MonthGroup<T> = { key: string; label: string; items: T[] };

/** Items in date order under "March 2026" headings (v1's SessionDateList),
 * months read in `timezone`. */
export function groupByMonth<T extends { startDateTime: string }>(items: T[], timezone: string): MonthGroup<T>[] {
  const sorted = [...items].sort((a, b) => (a.startDateTime > b.startDateTime ? 1 : a.startDateTime < b.startDateTime ? -1 : 0));
  const groups: MonthGroup<T>[] = [];
  for (const item of sorted) {
    const p = parts(item.startDateTime, timezone, { month: "long", year: "numeric" });
    const n = parts(item.startDateTime, timezone, { month: "2-digit", year: "numeric" });
    const key = `${n.year}-${n.month}`;
    let group = groups.find((g) => g.key === key);
    if (!group) {
      group = { key, label: `${p.month} ${p.year}`, items: [] };
      groups.push(group);
    }
    group.items.push(item);
  }
  return groups;
}

/** A click on a row of a list that holds up to `max` choices (v1's
 * SessionDateList): a chosen row is let go; a new one is added, and when the
 * list is full the oldest choice makes way, unless `switchable` is off, in
 * which case a full list takes nothing new. */
export function toggleSelection(selection: string[], id: string, max: number, switchable = true): string[] {
  if (selection.includes(id)) return selection.filter((s) => s !== id);
  if (selection.length >= max) {
    if (!switchable) return selection;
    return [...selection.slice(selection.length - max + 1), id];
  }
  return [...selection, id];
}

/** Adds rows to a list without repeating any already there. */
export function appendUnique<T extends { _id: string }>(current: T[], more: T[]): T[] {
  const seen = new Set(current.map((s) => s._id));
  return [...current, ...more.filter((s) => !seen.has(s._id) && (seen.add(s._id), true))];
}
